import React from "react";
import { t } from "../../i18n";
import { renderMarkdown } from "./renderMarkdown";

/**
 * What the panel shows of a turn while it streams. Kept apart from the transcript so a partial
 * reply is never persisted or settled: the settled bubble is always the turn's final reply.
 */
export interface LiveTurn {
  /** Tool rounds started so far; 0 until the first one. */
  step: number;
  /** The narration the model wrote alongside the latest tool round. */
  interim: string;
  /** Text streamed since the latest tool round started. */
  text: string;
}

export function withDelta(turn: LiveTurn | null, delta: string): LiveTurn {
  return { step: turn?.step ?? 0, interim: turn?.interim ?? "", text: (turn?.text ?? "") + delta };
}

// A step means the text streamed so far accompanied tool calls, so it was narration, not the reply.
export function withStep(turn: LiveTurn | null, step: number): LiveTurn {
  return { step, interim: turn?.text ?? "", text: "" };
}

function hasStreamed(turn: LiveTurn | null): boolean {
  return turn !== null && (turn.step > 0 || turn.text !== "");
}

interface Props {
  /** A turn has been sent and not yet settled. */
  pending: boolean;
  /** What the turn has streamed so far; null before its first event and once it settles. */
  turn: LiveTurn | null;
}

/**
 * The bubbles under the transcript while a turn is running: "Thinking…" until the first event,
 * then the tool-round progress with the latest narration, and the text streaming since that round.
 */
export const LiveTurnView = ({ pending, turn }: Props): JSX.Element => (
  <>
    {pending && !hasStreamed(turn) && (
      <div
        className="ai-chat__message ai-chat__message--assistant ai-chat__message--pending"
        data-testid="ai-chat-thinking"
      >
        {t("ai-chat.thinking", undefined, "Thinking…")}
      </div>
    )}
    {turn && turn.step > 0 && (
      <div
        className="ai-chat__message ai-chat__message--assistant ai-chat__message--pending"
        data-testid="ai-chat-progress"
      >
        <div data-testid="ai-chat-progress-step">{t("ai-chat.progress-step", { step: turn.step }, "Working… (step {{step}})")}</div>
        {turn.interim && (
          <div
            className="ai-chat__interim"
            data-testid="ai-chat-progress-interim"
          >
            {turn.interim}
          </div>
        )}
      </div>
    )}
    {turn?.text && (
      <div
        className="ai-chat__message ai-chat__message--assistant"
        data-testid="ai-chat-live-message"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(turn.text) }}
      />
    )}
  </>
);
